import React from 'react';
import { Link } from 'react-router-dom';
import { X, Minus, Plus, ShoppingBag, Trash2 } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useCurrency } from '../../context/CurrencyContext';
import { getImageUrl } from '../../utils/imageUrl';
import { formatPrice } from '../../utils/currency';

const CartDrawer = () => {
  const {
    items,
    isOpen,
    closeCart,
    removeItem,
    updateQuantity,
    getSubtotal,
    canCheckoutOnline,
  } = useCart();
  const { currency } = useCurrency();

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[60]"
        onClick={closeCart}
      />

      {/* Drawer */}
      <div className="fixed right-0 top-0 h-full w-full max-w-md bg-white z-[70] shadow-xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-medium flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            Shopping Cart
          </h2>
          <button
            onClick={closeCart}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="close-cart-btn"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-4">
          {items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <ShoppingBag className="w-16 h-16 mx-auto mb-4 opacity-20" />
              <p>Your cart is empty</p>
              <button
                onClick={closeCart}
                className="mt-4 text-black underline"
              >
                Continue shopping
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map((item) => (
                <div
                  key={`${item.artwork_id}-${item.size}`}
                  className="flex gap-4 p-3 bg-gray-50 rounded-lg"
                  data-testid={`cart-item-${item.artwork_id}`}
                >
                  <img
                    src={getImageUrl(item.image)}
                    alt={item.title}
                    className="w-20 h-20 object-cover rounded"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm truncate">{item.title}</h3>
                    <p className="text-xs text-gray-500">Size: {item.size}</p>
                    <p className="text-xs text-gray-500 capitalize">
                      Type: {item.artwork_type}
                    </p>
                    <p className="text-sm font-medium mt-1">
                      {formatPrice(item.unit_price, currency)}
                    </p>

                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border rounded">
                        <button
                          onClick={() =>
                            updateQuantity(item.artwork_id, item.size, item.quantity - 1)
                          }
                          className="p-1 hover:bg-gray-100"
                          data-testid={`decrease-qty-${item.artwork_id}`}
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 text-sm">{item.quantity}</span>
                        <button
                          onClick={() =>
                            updateQuantity(item.artwork_id, item.size, item.quantity + 1)
                          }
                          className="p-1 hover:bg-gray-100"
                          data-testid={`increase-qty-${item.artwork_id}`}
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      <button
                        onClick={() => removeItem(item.artwork_id, item.size)}
                        className="p-1 text-red-500 hover:bg-red-50 rounded"
                        data-testid={`remove-item-${item.artwork_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        {items.length > 0 && (
          <div className="border-t p-4 space-y-4">
            <div className="flex justify-between text-lg font-medium">
              <span>Subtotal</span>
              <span>{formatPrice(getSubtotal(), currency)}</span>
            </div>
            <p className="text-xs text-gray-500">
              Shipping calculated at checkout
            </p>

            {canCheckoutOnline() ? (
              <Link
                to="/checkout"
                onClick={closeCart}
                className="block w-full bg-black text-white text-center py-3 rounded-md hover:bg-gray-800 transition-colors"
                data-testid="checkout-btn"
              >
                Proceed to Checkout
              </Link>
            ) : (
              <div className="space-y-2">
                <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded">
                  Your cart contains paintings or framed prints that require a shipping quote.
                </p>
                <Link
                  to="/contact"
                  onClick={closeCart}
                  className="block w-full bg-black text-white text-center py-3 rounded-md hover:bg-gray-800 transition-colors"
                >
                  Contact for Shipping Quote
                </Link>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default CartDrawer;
